/**
 * The miniapp this site embeds.
 *
 * A constant, not configuration: gen.ai embeds one miniapp, and which one is a
 * fact about the product rather than a per-environment choice. The same id is
 * published on every environment — the registry resolves it to whichever
 * deployment that environment currently serves (see `lib/miniapp/registry`), so
 * the *version* varies while the identity does not.
 */
export const MINIAPP_PACKAGE_ID = 'com.picsart.genai-studiov2';
/**
 * The federated modules `genai-studio-v2` exposes, as named in its
 * `vite.config.ts`.
 *
 * A closed set, not configuration: these names are a build-time contract with
 * the remote, so a value outside this list cannot resolve however it is spelled.
 * They were environment variables, which made a typo in a deploy config look
 * like a runtime failure — and implied a choice nobody has.
 *
 * `Sidebar` is the third one the remote publishes: the standalone app, header
 * and all, which miniapp-shell mounts. It is absent here because mounting it on
 * gen.ai stacks a second header inside ours.
 */
export enum GenAiStudioModules {
  /**
   * The whole app with its own header suppressed, because this site already has
   * one. What a preset page wants: the entire browsing experience, embedded
   * rather than duplicated.
   */
  Embed = 'Embed',

  /**
   * The creations grid on its own — the whole of `/projects`.
   *
   * A separate module rather than a flag on `Embed`, because the two are mounted
   * in different places: the runtime mounts `Embed` once for the whole site,
   * while this is loaded by the route that shows it and torn down with it.
   *
   * A React component, not a `setup` factory: this app renders it in its own
   * tree (`loadMiniappComponent`), so it unmounts with the route.
   */
  Creations = 'Creations',
}

export const GEN_AI_BUNDLE_ID = 'com.picsart.apps.genai';

/**
 * Where the runtime renders `#miniapp-container` — a `<body>`-level node, as
 * miniapp-shell does (`miniappPortalTarget`). `RuntimeProvider` lives in the
 * sticky header, so without it the container rendered inside the header and
 * everything in it was capped at the header's stacking layer, under anything
 * the miniapp portals to `<body>`: a share sheet opened behind the mobile feed.
 */
export const MINIAPP_PORTAL_ROOT_ID = 'miniapp-portal-root';

/**
 * Tells the miniapp it is running on gen.ai web, sent in the runtime's
 * `defaultContext.headers`.
 *
 * The touchpoint header alone cannot say it: gen.ai sends the portfolio app's
 * own touchpoint (`GEN_AI_BUNDLE_ID`), so the miniapp reads it as the native
 * GenAI app. That is right for most of what it gates, but not all — on gen.ai
 * web the miniapp reads the full CMS catalogue rather than the app's
 * `/portfolio` one, and builds share links to gen.ai preset pages. A contract
 * with `genai-studio-v2` (`isGenaiWebHost` in `utils/environment/surface.ts`):
 * renaming either side silently turns those back into portfolio behaviour.
 *
 * The miniapp spreads these headers into its own API requests, so this also
 * reaches the CMS and Picsart APIs; their CORS preflights accept it.
 */
export const GENAI_HOST_HEADER = 'x-genai-host';
export const GENAI_HOST_WEB = 'web';
